import { Box, Paper, Stack, Typography } from "@mui/material";
import parse from "html-react-parser";
import React from "react";
import emptyIcon from "../../../assets/images/Icons/search-table.png";
import { CustomeDialog } from "../../../shared";
import PreparationStepsDialog from "../../Recipe/components/PreparationStepsDialog";

const ContentSection = ({ children, title, backgroundColor = "#F7FAFC" }) => {
  return (
    <Stack>
      <Stack
        direction="row"
        alignItems="center"
        height="54px"
        borderBottom="1px solid"
        borderColor="divider"
      >
        <Typography
          variant="h6"
          fontWeight={600}
          lineHeight="19px"
          minWidth="160px"
        >
          {title}
        </Typography>
      </Stack>
      <Stack sx={{ backgroundColor }}>{children}</Stack>
    </Stack>
  );
};

const EmptyComponent = ({ message }) => {
  return (
    <Stack alignItems="center" gap="8px" backgroundColor="#F7FAFC" py="32px">
      <img src={emptyIcon} alt="empty" height={56} width={56} />
      <Typography sx={{ fontWeight: 400, fontSize: "14px", color: "#485364" }}>
        {message ?? "No matches found"}
      </Typography>
    </Stack>
  );
};

export default function TipsAndProcedureBasicDetails({
  tipsAndTricksDetails,
  handleVideoClick,
  tipsAndTricks,
  onImageClick,
}) {
  const preparationSteps = (steps) =>
    steps.map((item, index) => {
      return (
        <div key={index}>
          <Stack
            sx={{
              flexDirection: "row",
              gap: "8px",
              padding: "8px",
            }}
          >
            {item.image_url ? (
              <Box
                component="img"
                src={item.image_url}
                sx={{
                  height: "60px",
                  width: "60px",
                  backgroundColor: "transparent",
                  cursor: "pointer",
                  borderRadius: "8px",
                  backgroundSize: "contain",
                  backgroundPosition: "center",
                  objectFit: "cover",
                  transition:
                    "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                  "&:hover": {
                    transform: "scale(1.05)",
                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                  },
                }}
                onClick={() => handleVideoClick(true, item)}
              />
            ) : (
              <Box
                sx={{
                  height: "60px",
                  minWidth: "60px",
                  cursor: "pointer",
                  borderRadius: "6px",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#F5F8FC",
                  border: 1,
                  borderColor: "divider",
                }}
                onClick={() => handleVideoClick(true, item)}
              >
                <img src="/icons/ic_image.svg" alt="" />
              </Box>
            )}

            <Stack>
              <Typography fontSize="12px" fontWeight={600} color="#657083">
                STEP {item?.step_number}
              </Typography>
              <Typography
                fontSize="14px"
                fontWeight={400}
                sx={{
                  "& ul, & ol": {
                    marginInlineStart: "15px",
                  },
                }}
              >
                {parse(item?.steps ?? "")}
              </Typography>
            </Stack>
          </Stack>
        </div>
      );
    });

  return (
    <>
      <ContentSection title="Method">
        {tipsAndTricksDetails?.data?.method &&
        tipsAndTricksDetails?.data?.method !== "<p><br></p>" ? (
          <Stack
            sx={{
              p: "20px",
              "& ul, & ol": {
                paddingInline: "20px",
              },
            }}
          >
            {parse(
              tipsAndTricksDetails?.data?.method
                ? tipsAndTricksDetails?.data?.method
                : "",
            )}
          </Stack>
        ) : (
          <EmptyComponent message="No method added yet!" />
        )}
      </ContentSection>

      <ContentSection title="Preparation Steps" backgroundColor="transparent">
        {tipsAndTricksDetails?.data?.steps?.length ? (
          preparationSteps(tipsAndTricksDetails?.data?.steps)
        ) : (
          <EmptyComponent message="No preparation steps added yet!" />
        )}
      </ContentSection>

      <ContentSection title="Files" backgroundColor="transparent">
        {tipsAndTricksDetails.data.images?.length ? (
          <Stack
            display="flex"
            flexWrap="wrap"
            flexDirection="row"
            justifyContent="start"
            alignItems="start"
            gap="8px"
            py="8px"
          >
            {tipsAndTricksDetails.data.images?.map((file, index) => (
              <Stack
                key={index}
                component={Paper}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  padding: "8px",
                  width: { md: "229px", xs: "100%" },
                  backgroundColor: "#F5F8FC",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: "6px",
                  gap: "6px",
                }}
              >
                <Box
                  sx={{
                    width: "32px",
                    height: "32px",
                    backgroundColor: "#1760D21F",
                    borderRadius: "50%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Box
                    component="img"
                    src="/icons/ic_file.svg"
                    alt="file icon"
                  />
                </Box>
                <Box
                  flex={1}
                  overflow="hidden"
                  sx={{
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    component="a"
                    href={file?.doc_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      textDecoration: "none",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      color: "text.primary",
                    }}
                  >
                    {file?.doc_name}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
        ) : (
          <EmptyComponent message="No files added" />
        )}
      </ContentSection>

      <CustomeDialog
        PaperProps={{
          sx: {
            minWidth: { lg: "55vw", md: "80vw", xs: "90%" },
            maxWidth: { lg: "55vw", md: "80vw", xs: "90%" },
          },
        }}
        closeIcon
        handleClose={() => handleVideoClick(false, [])}
        open={tipsAndTricks?.open}
        divider={false}
        title="Preparation Steps"
        content={
          <PreparationStepsDialog
            state={tipsAndTricks.items}
            handleClose={handleVideoClick}
            data={tipsAndTricksDetails?.data?.steps}
            onImageClick={onImageClick}
          />
        }
      />
    </>
  );
}
